import React, { useState } from 'react';
import { ExbabelLoading } from '@/components/ui/ExbabelLoading';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription } from '@/components/ui/alert';

export function ResetPasswordPage() {
    const navigate = useNavigate();
    const { updatePassword, isAuthenticated, loading: authLoading } = useAuth();
    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(false);

    const isInvite = window.location.hash.includes('type=invite');
    const searchParams = new URLSearchParams(window.location.search);
    const redirectPath = searchParams.get('redirect') || '/';

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        if (!isAuthenticated) {
            setError('Your security session has expired. Please request a new link.');
            return;
        }

        setLoading(true);
        setError(null);

        if (password.length < 6) {
            setError('Password must be at least 6 characters');
            setLoading(false);
            return;
        }

        if (password !== confirmPassword) {
            setError('Passwords do not match');
            setLoading(false);
            return;
        }

        const { error } = await updatePassword(password);

        if (error) {
            setError(error.message || 'Failed to update password');
            setLoading(false);
        } else {
            setSuccess(true);
            setLoading(false);
            // Wait a moment and redirect to home or custom path
            setTimeout(() => {
                navigate(redirectPath);
            }, 2000);
        }
    };

    return (
        <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-blue-100 p-4">
            <Card className="w-full max-w-md">
                <CardHeader className="text-center">
                    <CardTitle className="text-2xl font-bold">
                        {isInvite ? 'Finish Account Setup' : 'Set New Password'}
                    </CardTitle>
                    <CardDescription>
                        {isInvite 
                          ? "Welcome to Exbabel! Please set a password for your new account."
                          : "Please enter your new password below to secure your account."}
                    </CardDescription>
                </CardHeader>
                <CardContent>
                    {authLoading ? (
                        <div className="flex flex-col items-center justify-center py-12 space-y-4">
                            <ExbabelLoading label="Validating your security link" />
                            <p className="text-sm text-muted-foreground">Validating your security link...</p>
                        </div>
                    ) : !isAuthenticated ? (
                        <div className="text-center space-y-4 py-8">
                            <div className="text-4xl">❌</div>
                            <p className="text-red-700 font-medium">Link Expired or Invalid</p>
                            <p className="text-sm text-gray-500">
                                This link may have expired or already been used. 
                                Please request a new password reset link from the login page.
                            </p>
                            <Button variant="outline" onClick={() => navigate('/signin')}>
                                Back to Login
                            </Button>
                        </div>
                    ) : success ? (
                        <div className="text-center space-y-4 py-4">
                            <div className="text-4xl">✅</div>
                            <p className="text-green-700 font-medium">Password updated successfully!</p>
                            <p className="text-sm text-gray-500 text-center">Redirecting you to the app...</p>
                        </div>
                    ) : (
                        <form onSubmit={handleSubmit} className="space-y-4">
                            {error && (
                                <Alert variant="destructive">
                                    <AlertDescription>{error}</AlertDescription>
                                </Alert>
                            )}

                            <div className="space-y-2">
                                <Label htmlFor="password">New Password</Label>
                                <Input
                                    id="password"
                                    type="password"
                                    placeholder="••••••••"
                                    value={password}
                                    onChange={(e) => setPassword(e.target.value)}
                                    required
                                    disabled={loading}
                                />
                            </div>

                            <div className="space-y-2">
                                <Label htmlFor="confirm-password">Confirm Password</Label>
                                <Input
                                    id="confirm-password"
                                    type="password"
                                    placeholder="••••••••"
                                    value={confirmPassword}
                                    onChange={(e) => setConfirmPassword(e.target.value)}
                                    required
                                    disabled={loading}
                                />
                            </div>

                            <Button type="submit" className="w-full" disabled={loading}>
                                {loading ? 'Updating password...' : 'Set Password'}
                            </Button>
                        </form>
                    )}
                </CardContent>
            </Card>
        </div>
    );
}
